'use client'

import { getSupabaseClient } from '@/lib/supabase-client'
import { getValidatedAccessToken } from '@/lib/supabase-validated-session'
import { captureEvent, identifyUser } from '@/instrumentation-client'
import { trackConversion } from '@/lib/google-analytics'
import { parsePreferredLanguage } from '@/server/domain/language'
import { parseAccountSnapshot } from '@/lib/account-snapshot'
import { readCookie } from '@/lib/cookies'
import {
  getCachedLanguage,
  resetClientAuthState,
  type AuthCompletionSource,
  type AuthOperationResult,
  type AuthStateHandlers,
  type FetchUserDataInput,
  type User
} from '@/contexts/auth-operations'
import { clearPendingSignup, readPendingSignup, type PendingAuthSource } from '@/lib/pending-signup'
import { accountPersonProperties } from '@/lib/account-person-properties'
import { clearAcquisitionSource, readAcquisitionSource } from '@/lib/acquisition-capture'

export interface SyncInFlightRef {
  current: {
    key: string
    promise: Promise<AuthOperationResult>
  } | null
}

export type SyncAttemptResult = AuthOperationResult & {
  httpStatus?: number
}

/** La cuenta sincronizada: el snapshot (que valida `parseAccountSnapshot`) más su id. */
interface SyncedUserDto {
  id: string
}

interface SyncAccountResponse {
  success: true
  isNewUser: boolean
  linkedByEmail?: boolean
  user: SyncedUserDto
}

interface SyncAccountInput {
  accessToken: string
  /**
   * Locale crudo del intento, tal y como lo conoce el caller (OTP y login
   * social). La resolución a `preferredLanguage` es ÚNICA y vive en
   * `attemptAccountSync`; el caller no pre-resuelve nada.
   */
  locale?: string
  source: AuthCompletionSource
  handlers: AuthStateHandlers
}

const supabase = getSupabaseClient()
const SESSION_RESTORE_KEY = 'session-restore'
const SIGNUP_CONVERSION_ID = 'AW-17789051629/OtZ1CNOP6c4bEO3FvaJC'

function dedupeSync(
  key: string,
  syncInFlightRef: SyncInFlightRef,
  run: () => Promise<AuthOperationResult>
): Promise<AuthOperationResult> {
  if (syncInFlightRef.current?.key === key) {
    return syncInFlightRef.current.promise
  }

  const syncPromise = run().finally(() => {
    if (syncInFlightRef.current?.promise === syncPromise) {
      syncInFlightRef.current = null
    }
  })

  syncInFlightRef.current = { key, promise: syncPromise }
  return syncPromise
}

function toSyncedUserData(syncedUser: SyncedUserDto): User {
  return { id: syncedUser.id, ...parseAccountSnapshot(syncedUser) }
}

function applySyncedUserData(
  data: SyncAccountResponse,
  handlers: AuthStateHandlers
): User {
  const userData = toSyncedUserData(data.user)

  handlers.setUser(userData)
  identifyUser(userData.id, accountPersonProperties(userData))

  return userData
}

function captureAuthCompleted({ source, isNewUser, user }: {
  source: AuthCompletionSource
  isNewUser: boolean
  user: User
}): void {
  if (source === 'session') {
    return
  }

  if (isNewUser) {
    trackConversion(SIGNUP_CONVERSION_ID)
  }

  const eventNameBySource: Record<PendingAuthSource, string> = {
    apple: 'auth_apple_completed',
    google: 'auth_google_completed',
    otp: 'auth_otp_verified'
  }

  // De dónde vino el alta (DD17): solo en altas nuevas, y se consume al emitir.
  // Sin marca, las props son las de siempre (el método va en el nombre).
  const acquisitionSource = isNewUser ? readAcquisitionSource() : null

  captureEvent(eventNameBySource[source], {
    email: user.email,
    success: true,
    isNewUser,
    locale: user.preferredLanguage,
    ...(acquisitionSource ? { acquisition_source: acquisitionSource } : {})
  })

  if (acquisitionSource) {
    clearAcquisitionSource()
  }
}

async function attemptAccountSync({
  accessToken,
  locale,
  source,
  handlers
}: SyncAccountInput): Promise<SyncAttemptResult> {
  try {
    // Único resolutor del idioma del sync, con un solo orden para todos los
    // flujos: intención explícita del intento → cookie de ESTE intento de auth
    // (la escribe `storeAuthLanguage` al empezar OAuth web, OTP y nativo) →
    // residuo de sesiones previas. Sin señal no se inventa el idioma: el campo
    // se omite y el alta ya cae al default del servidor (es).
    const cachedLanguage = getCachedLanguage()
    const language =
      parsePreferredLanguage(locale) ||
      parsePreferredLanguage(readCookie('auth-language')) ||
      cachedLanguage ||
      undefined
    // El alta en curso (`?ref` o código manual, ADR-013; casilla de marketing,
    // ADR-047) viaja en el MISMO fetch de sync, y cada campo solo si existe.
    const { referredByCode, marketingConsent } = readPendingSignup()

    const response = await fetch('/api/auth/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        ...(language ? { preferredLanguage: language } : {}),
        ...(referredByCode ? { referredByCode } : {}),
        ...(marketingConsent ? { marketingConsent: true } : {})
      })
    })

    if (!response.ok) {
      return {
        success: false,
        error: 'Failed to synchronize account',
        httpStatus: response.status
      }
    }

    const data = await response.json() as SyncAccountResponse
    const userData = applySyncedUserData(data, handlers)
    captureAuthCompleted({
      source,
      isNewUser: Boolean(data.isNewUser),
      user: userData
    })

    return { success: true, isNewUser: Boolean(data.isNewUser) }
  } catch (error) {
    console.error('Error fetching user data:', error)
    return { success: false, error: 'Error de conexión' }
  }
}

async function tearDownInvalidAuthSession(handlers: AuthStateHandlers): Promise<void> {
  resetClientAuthState(handlers)
  await supabase.auth.signOut()
}

async function syncOnce(
  accessToken: string | null,
  input: Omit<SyncAccountInput, 'accessToken'>
): Promise<SyncAttemptResult> {
  if (!accessToken) {
    return { success: false, error: 'Failed to synchronize account' }
  }

  return attemptAccountSync({ accessToken, ...input })
}

async function syncWithTokenRetry(
  handlers: AuthStateHandlers
): Promise<AuthOperationResult> {
  const sessionInput = { source: 'session' as const, handlers }

  const firstResult = await syncOnce(await getValidatedAccessToken(), sessionInput)
  if (firstResult.success || firstResult.httpStatus !== 401) {
    return firstResult
  }

  const retryResult = await syncOnce(await getValidatedAccessToken(), sessionInput)
  if (retryResult.success || retryResult.httpStatus !== 401) {
    return retryResult
  }

  await tearDownInvalidAuthSession(handlers)
  return { success: false, error: 'Failed to synchronize account' }
}

async function runSignInAccountSync(input: SyncAccountInput): Promise<AuthOperationResult> {
  const { handlers } = input

  try {
    const result = await attemptAccountSync(input)

    if (!result.success && result.httpStatus === 401) {
      await tearDownInvalidAuthSession(handlers)
    }

    return result
  } finally {
    // El alta en curso se borra entera tras el sync de sign-in, para que un sync
    // posterior nunca re-atribuya ni re-aplique la casilla (el servidor ya los
    // selló una vez, si creó la cuenta).
    clearPendingSignup()
    handlers.setIsLoading(false)
  }
}

async function runSessionRestoreSync(
  handlers: AuthStateHandlers
): Promise<AuthOperationResult> {
  try {
    return await syncWithTokenRetry(handlers)
  } finally {
    handlers.setIsLoading(false)
  }
}

export function fetchSynchronizedUser({
  accessToken,
  locale,
  source,
  syncInFlightRef,
  handlers
}: FetchUserDataInput & {
  syncInFlightRef: SyncInFlightRef
  handlers: AuthStateHandlers
}): Promise<AuthOperationResult> {
  return dedupeSync(accessToken, syncInFlightRef, () =>
    runSignInAccountSync({ accessToken, locale, source, handlers })
  )
}

export function fetchSessionRestoreSync({
  syncInFlightRef,
  handlers
}: {
  syncInFlightRef: SyncInFlightRef
  handlers: AuthStateHandlers
}): Promise<AuthOperationResult> {
  return dedupeSync(SESSION_RESTORE_KEY, syncInFlightRef, () =>
    runSessionRestoreSync(handlers)
  )
}

export function fetchAfterSignInSync({
  accessToken,
  syncInFlightRef,
  handlers
}: {
  accessToken: string
  syncInFlightRef: SyncInFlightRef
  handlers: AuthStateHandlers
}): Promise<AuthOperationResult> | null {
  const { source } = readPendingSignup()
  if (!source) {
    handlers.setIsLoading(false)
    return null
  }

  return fetchSynchronizedUser({
    accessToken,
    source,
    syncInFlightRef,
    handlers
  })
}
